import { useState } from 'react';

import { AddProfileImage } from '@components/add-profile-image';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { IconEdit } from '@components/icons/edit';
import { IconTravellerCreate } from '@components/icons/traveller-create';
import { SelectOption } from '@components/selectmulti';
import { Sidebar, forceHideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import {
  useCompanyTraveller,
  useCreateCompanyTraveller,
  useEditCompanyTraveller,
} from 'api/company-traveller/hooks';
import { loadCompanyTravellers } from 'api/company-traveller/requests';
import { useActiveUsers } from 'api/users/hooks';
import { AttributeFields } from 'components/attribute-fields';
import {
  appendAttributeValues,
  companyTravellerAttributePrefix,
} from 'ui/authenticated/main/helpers';

export const useCompanyTravellerSidebar = () => {
  const sidebar = useSidebar(CreateCompanyTraveller, {
    variant: 'LARGE',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type TravellerFormBaseFields = {
  name: string;
  email: string;
  phone: string;
};

type CompanyTravellerAttributes = {
  [K in `company_traveller_attribute__${string}`]: string | File | File[] | Date | SelectOption[];
};

type TravellerFormData = TravellerFormBaseFields & CompanyTravellerAttributes;

const Form = createForm<TravellerFormData>();

type CreateCompanyTravellerProps = {
  companyTravellerShortId?: string;
  workspaceId?: string;
  onBacktrack?: () => void;
};

const CreateCompanyTraveller = ({
  companyTravellerShortId,
  onBacktrack,
  workspaceId,
}: CreateCompanyTravellerProps) => {
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data ?? [];
  const companyTravellerQuery = useCompanyTraveller(companyTravellerShortId ?? '');
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const createMutation = useCreateCompanyTraveller(forceHideSidebar);
  const editMutation = useEditCompanyTraveller(forceHideSidebar);

  const userQuery = useActiveUsers();

  const usersData = userQuery.data ?? [];

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);
  const [isPictureInvalid, setIsPictureInvalid] = useState(false);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const isDirty = formStore.useIsDirty() || !!picture || clearPicture;
  const companyTraveller = companyTravellerQuery.data;

  const isEdit = !!companyTraveller;

  const email = formStore.useFieldValue('email') as string;
  const formErrors: { [key: string]: string } = {};

  const checkEmail = async () => {
    if (!email) return;
    const response = await loadCompanyTravellers(`?email=${email}&page_size=1&page=1`);
    if (response.results.length > 0) {
      formStore.actions.updateErrors({ email: t('travellerExists') });
    }
  };

  const onFileMultiRemove = (file: string) => {
    setFilesToRemove([...filesToRemove, file]);
  };

  const handleSave = (data: TravellerFormData) => {
    if (isPictureInvalid) {
      return;
    }

    if (Object.keys(formErrors).length > 0) {
      return formStore.actions.setErrors(formErrors);
    }
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    if (workspaceId) formData.append('workspace', workspaceId);

    if (companyTravellerAttributes) {
      appendAttributeValues({
        formData,
        data,
        attributes: companyTravellerAttributes,
        prefix: companyTravellerAttributePrefix,
        filesToRemove,
      });
    }

    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    if (isEdit) {
      return editMutation.mutateAsync({ shortId: companyTraveller.shortId, data: formData });
    }
    return createMutation.mutateAsync(formData);
  };

  const onCancelSidebar = () => {
    forceHideSidebar();
    if (onBacktrack) onBacktrack();
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={isEdit ? t('editTraveller') : t('createTraveller')}
        icon={isEdit ? IconEdit : IconTravellerCreate}
        iconColor="elementBlue100"
      />
      <Sidebar.Content>
        {companyTravellerAttributeTemplatesQuery.isLoading ||
        (!companyTraveller && companyTravellerShortId) ? (
          <Sidebar.Spinner />
        ) : (
          <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
            <Stack space={4}>
              <Stack space={4} orientation="horizontal" align="center">
                <AddProfileImage
                  name={companyTraveller?.name ?? ''}
                  initalPicture={companyTraveller?.picture ?? ''}
                  picture={picture}
                  setPicture={setPicture}
                  clearPicture={clearPicture}
                  setClearPicture={setClearPicture}
                  setIsPictureInvalid={setIsPictureInvalid}
                />
                <div>
                  <Text font="semibold14" text={t('picture')} />
                  <Text font="regular14" text={t('acceptedFiles')} color="textHelper" />
                  {isPictureInvalid && <FormError text={t('invalidFileType')} />}
                </div>
              </Stack>
              <Form.TextField
                id="name"
                label={t('core.fullName')}
                validators={['required']}
                defaultValue={companyTraveller?.name ?? ''}
              />
              <Form.TextField
                id="email"
                label={t('core.email')}
                validators={['required', 'email']}
                onBlur={checkEmail}
                defaultValue={companyTraveller?.email ?? ''}
              />
              <Form.PhoneField
                id="phone"
                label={t('core.phone')}
                validators={['phone']}
                defaultValue={companyTraveller?.phone ?? ''}
              />
              {companyTravellerAttributes?.map((attribute) => {
                const traveller_attribute = companyTraveller?.companyTravellerAttributes.find(
                  (attr) => attr.attributeShortId === attribute.shortId,
                );
                return (
                  attribute.isActive && (
                    <AttributeFields
                      key={attribute.shortId}
                      attributeTemplate={attribute}
                      prefix={companyTravellerAttributePrefix}
                      label={attribute.templateTitle}
                      usersData={usersData}
                      attribute={traveller_attribute}
                      onFileMultiRemove={onFileMultiRemove}
                    />
                  )
                );
              })}
            </Stack>
            <Sidebar.Footer>
              <Form.Actions>
                <Form.Reset text={t('core.cancel')} variant="secondary" onClick={onCancelSidebar} />
                <Form.Submit
                  text={isEdit ? t('core.save') : t('createTraveller')}
                  variant="primary-blue"
                  isDisabled={isEdit && !isDirty}
                />
              </Form.Actions>
            </Sidebar.Footer>
          </FormWithUnsavedChangesPrompt>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    createTraveller: 'Create traveller',
    editTraveller: 'Edit traveller',
    travellerAttributes: 'Traveller attributes',
    travellerExists: 'Traveller with this email already exists',
    picture: 'Profile image',
    acceptedFiles: 'Accepted file types: .png and .jpg',
    invalidFileType: 'This file type is not supported',
  },
};
